import { useState, useEffect, useCallback } from 'react'
import { 
  LineChart,
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts'
import { getCumulativeTotalSoldData } from '../../supabase-client'

// Custom tooltip with shadcn styling
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-border rounded-lg p-3 shadow-md">
        <p className="text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-sm" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">
                Sales Velocity
              </span>
            </div>
            <span className="font-medium">
              {entry.value.toFixed(1)}kg/period
            </span>
          </div>
        ))}
      </div>
    )
  }
  return null
}

export default function SalesVelocityChart({ dateRange: externalDateRange, loading: externalLoading, compact = false }) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)

  // Use external date range if provided
  const dateRange = externalDateRange || 'all'

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const cumulativeData = await getCumulativeTotalSoldData(dateRange)
      
      // Calculate velocity (rate of change) from cumulative data
      // Exclude the current/most recent period as it's still developing
      const velocityData = []
      const dataToProcess = cumulativeData.length > 1 ? cumulativeData.slice(0, -1) : cumulativeData
      
      for (let i = 1; i < dataToProcess.length; i++) {
        const current = dataToProcess[i]
        const previous = dataToProcess[i - 1]
        
        // Calculate velocity as the difference between consecutive periods
        const velocity = current.cumulativeTotal - previous.cumulativeTotal
        
        velocityData.push({
          period: current.period,
          velocity: velocity,
          displayYear: current.displayYear,
          isH1: current.isH1
        })
      }
      
      setData(velocityData)
    } catch (error) {
      console.error('Error loading sales velocity data:', error)
      setData([])
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  useEffect(() => {
    loadData()
  }, [loadData])

  const isLoading = externalLoading !== undefined ? externalLoading : loading
  
  if (isLoading) {
    return (
      <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
        <div className="reports-loading">
          Loading sales velocity data...
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
        <div className="reports-loading">
          No sales velocity data available for the selected period
        </div>
      </div>
    )
  }

  // Calculate average velocity for reference line
  const avgVelocity = data.reduce((sum, item) => sum + item.velocity, 0) / data.length

  return (
    <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid 
            strokeDasharray="none" 
            stroke="var(--border)" 
            strokeWidth="1"
            opacity="0.3"
          />
          
          {/* Average velocity reference line */}
          <ReferenceLine 
            y={avgVelocity} 
            stroke="#6366F1" 
            strokeWidth="2" 
            opacity="0.6"
            strokeDasharray="5 5"
            label={{ 
              value: `Avg: ${avgVelocity.toFixed(1)}kg`, 
              position: "insideTopRight",
              style: { fontSize: '12px', fill: '#6366F1' }
            }}
          />
          
          {/* Minor X-axis reference lines for H2 periods */}
          {data.map((item) => {
            if (!item.isH1) { // H2 periods get minor grid lines
              return (
                <ReferenceLine 
                  key={`minor-x-${item.period}`}
                  x={item.period} 
                  stroke="var(--border)" 
                  strokeWidth="0.5" 
                  opacity="0.15"
                  strokeDasharray="none"
                />
              )
            }
            return null
          })}
          
          <XAxis 
            dataKey="period"
            axisLine={true}
            tickLine={true}
            tick={(props) => {
              const { x, y, payload } = props
              const dataItem = data.find(item => item.period === payload.value)
              
              if (!dataItem) return null
              
              // Only show year labels for H1 (first half of year)
              if (dataItem.isH1) {
                return (
                  <g>
                    {/* Major tick mark */}
                    <line 
                      x1={x} 
                      y1={y - 10} 
                      x2={x} 
                      y2={y} 
                      stroke="var(--muted-foreground)" 
                      strokeWidth={2}
                    />
                    {/* Year label */}
                    <text 
                      x={x} 
                      y={y + 15} 
                      textAnchor="middle" 
                      fill="var(--muted-foreground)"
                      fontSize={12}
                      fontFamily="Geist, -apple-system, BlinkMacSystemFont, sans-serif"
                    >
                      {dataItem.displayYear}
                    </text>
                  </g>
                )
              } else {
                // Minor tick mark for H2
                return (
                  <g>
                    <line 
                      x1={x} 
                      y1={y - 5} 
                      x2={x} 
                      y2={y} 
                      stroke="var(--muted-foreground)" 
                      strokeWidth={1}
                    />
                  </g>
                )
              }
            }}
          />
          <YAxis 
            axisLine={true}
            tickLine={true}
            tick={{ 
              fontSize: 12, 
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            label={{ 
              value: 'Sales Velocity (kg/period)', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: '12px', fill: 'var(--muted-foreground)' }
            }}
            domain={[0, 'dataMax']}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line 
            type="monotone" 
            dataKey="velocity" 
            stroke="#F54A00" 
            strokeWidth={3}
            dot={{ fill: '#F54A00', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, fill: '#F54A00' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}